import { runInAction } from 'mobx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdTagCreative, DisplayCreative } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import type { ICreativeFlowBaseParams } from '../creatives.types';
import { findDimensionBySpec, getAdTagCreativeAttributes } from './AddAdTagCreative.utils';

// =============================================================================
// Types
// =============================================================================

/** Steps in the Add Ad Tag Creative flow. */
export type TAddAdTagCreativeStep = 'dimensions' | 'creativeModal';

export type IUseAddAdTagCreativeParams = ICreativeFlowBaseParams;

export interface IUseAddAdTagCreativeReturn {
  /** Current step in the flow. */
  step: TAddAdTagCreativeStep;
  /** The ephemeral ad tag creative being configured (undefined during dimension step). */
  creative: AdTagCreative | undefined;
  /** The currently selected dimension spec (for the dimension step). */
  selectedSpec: string | undefined;
  /** Whether the creative is currently being saved. */
  isSaving: boolean;
  /** Handles dimension selection in the dimensions modal. */
  handleDimensionSelect: (spec: string) => void;
  /** Proceeds from dimension selection to the creative modal. */
  handleDimensionNext: () => void;
  /** Handles Back from the dimension step. */
  handleDimensionBack: () => void;
  /** Persists the creative to the backend and closes the modal. */
  handleSave: () => Promise<void>;
  /** Discards the ephemeral creative and closes. */
  handleCancel: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Manages the two-step state for the AddAdTagCreative flow.
 *
 * Step 1: User selects dimensions from a modal.
 * Step 2: CreativeModal opens with the creative pre-configured with selected dimensions.
 *
 * The creative is not created until the user confirms their dimension selection,
 * ensuring it is initialized with the correct width, height, and spec.
 *
 * @param params - Hook parameters.
 * @returns Step state, creative instance, and handler functions.
 */
export function useAddAdTagCreative(
  params: IUseAddAdTagCreativeParams,
): IUseAddAdTagCreativeReturn {
  const {
    campaignId,
    eventId,
    creatives: creativesArray,
    onAppear,
    onComplete,
    onBack,
    onCancel,
  } = params;
  const { t } = useTranslation();
  const { Creatives } = useStore();
  const account = useAccount();

  const [step, setStep] = useState<TAddAdTagCreativeStep>('dimensions');
  const [selectedSpec, setSelectedSpec] = useState<string | undefined>(undefined);
  const [creative, setCreative] = useState<AdTagCreative | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const handleDimensionSelect = useCallback((spec: string): void => {
    setSelectedSpec(spec);
  }, []);

  const handleDimensionNext = useCallback((): void => {
    if (!selectedSpec) {
      return;
    }
    const dimension = findDimensionBySpec(selectedSpec);
    if (!dimension) {
      return;
    }

    const attributes = getAdTagCreativeAttributes({
      campaignId,
      eventId,
      accountId: account.id,
      spec: selectedSpec,
      width: dimension.width,
      height: dimension.height,
    });
    const newCreative = Creatives.create(attributes) as AdTagCreative;
    setCreative(newCreative);
    setStep('creativeModal');
  }, [Creatives, account.id, campaignId, eventId, selectedSpec]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!creative) {
      return;
    }
    setIsSaving(true);
    try {
      const savedCreative = (await Creatives.add(creative, {
        refreshApiCache: false,
      })) as DisplayCreative;
      if (creativesArray) {
        runInAction(() => {
          creativesArray.unshift(savedCreative);
        });
        onAppear?.(savedCreative.id);
      }
      onComplete();
    } catch {
      toast(t('Failed to save creative "{{name}}"', { name: creative.get('name') }), {
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [Creatives, creative, creativesArray, onAppear, onComplete, t]);

  return {
    step,
    creative,
    selectedSpec,
    isSaving,
    handleDimensionSelect,
    handleDimensionNext,
    handleDimensionBack: onBack,
    handleSave,
    handleCancel: onCancel,
  };
}
